/**
 * Use as a Vue plugin:
 *
 * In *.entry.js file:
 * import format from '_share_/plugins/format';
 * Vue.use(format);
 *
 * In component files:
 * Within <template>: $format.<FUNCTION>
 * Within <script>: this.$format.<FUNCTION>
 *
 * Use elsewhere:
 *
 * import format from '_shared_/plugins/format';
 * const $format = format.formatHelpers;
 * Call with $format.<FUNCTION>
 *
 * The helper functions, our default error string,
 * _and_ numeral itself will be available on
 * the this.$format object.
 *
 * These two are therefore equivalent:
 *
 * this.$format.number(1049024);
 * this.$format.numeral(1049024).format(0,0);
 */

import numeral from 'numeral';

const defaultErrorString = '--';
const defaultErrorCode = -32768;
const invalidValues = [
  defaultErrorString,
  defaultErrorCode,
  String(defaultErrorCode),
  -99999,
  '-99999'
];

// Set the default abbreviations
numeral.localeData('en').abbreviations = {
  thousand: 'K',
  million: 'M',
  billion: 'B',
  trillion: 'T'
};

const formatHelpers = {
  numeral,
  defaultErrorString,
  number,
  rawValue,
  absoluteRawValue,
  price,
  pricePosNeg,
  priceMagnitude,
  histDistPrice,
  rawPercent,
  percent,
  percentPosNeg,
  camelSpace,
  string,
  name,
  fullDate,
  longDate,
  shortDate,
  shortDatePreFormatted,
  fromOADateToShortString,
  dateYear,
  dateTime,
  fromOADate,
  formatExtraZero,
  yearString,
  removeTabsAndLineBreaks,
  convertStringToNumber
};

/*===========================================
=            NUMERICAL FUNCTIONS            =
===========================================*/
function rawValue(amt) {
  if (typeof amt === 'undefined'
		|| amt === null
		|| invalidValues.includes(amt)
		|| (typeof amt === 'string' && Number(amt) === defaultErrorCode && amt.includes(defaultErrorCode))
  ) {
    return defaultErrorString;
  }

  const val = numeral(amt).value();

  return numeral(amt).value() === null
    ? defaultErrorString
    : val;
}

function absoluteRawValue(amt) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }
  return Math.abs(val);
}

function number(amt, precision) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }

  const format = precision ? `0,0.${getZeroes(precision)}` : '0,0';

  return numeral(val).format(format);
}

function price(amt, precision = 2) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }
  const format = `$0,0.${getZeroes(precision)}`;

  return numeral(val).format(format);
}

function pricePosNeg(amt, precision) {
  const val = price(amt, precision);
  if (amt > 0) {
    return `+${val}`;
  }

  return val;
}

function priceMagnitude(amt) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }

  return numeral(val).format('($0a)');
}

//CG-15706 Cap gains has a specific format
//0 is represented as 0.00
//Zeroes are trimmed after precision 2
function histDistPrice(amt) {
  let val = '$0.00';
  if (//amt is not null or error
    amt !== null
		&& amt !== undefined
		&& !isNaN(amt)
		&& amt !== defaultErrorString
		&& amt !== defaultErrorCode) {
    //toString trims all trailing zeroes
    const amtStr = amt.toString();
    const amtArray = amtStr.split('.');

    const numerals = amtArray[0];
    let decimals = amtArray[1] || '';
    decimals = decimals.split('');
    //remove trailing zeros
    while (decimals[decimals.length - 1] === '0') {
      decimals.pop();
    }
    //Adds zeroes if there are less than 2 decimals
    while (decimals.length < 2) {
      decimals.push('0');
    }
    //precision of 4 decimal places without rounding
    decimals = decimals.slice(0, 4);
    //Remove any zeroes from last 2 decimal places, but only if 4th decimal is 0
    //i.e. If 1.2104, keep at 1.2104. If 1.200, should return 1.20
    if (decimals[decimals.length - 1] === '0') {
      for (let i = decimals.length - 1; i > 1; i--) {
        if (decimals[i] === '0') {
          decimals.pop();
        }
      }
    }
    val = `$${numerals}.${decimals.join('')}`;
  }
  return val;
}

// Use if your value comes back already in percentage form,
// i.e. 1.25 should = 1.25% as opposed to 125%
function rawPercent(amt, precision) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }

  return precision ? `${number(val, precision)}%` : `${val}%`;
}

function percent(amt, precision) {
  const val = rawValue(amt);
  if (val === defaultErrorString) {
    return val;
  }

  const format = precision ? `0.${getZeroes(precision)}%` : '0%';
  return numeral(val).format(format);
}

function percentPosNeg(amt, precision) {
  let val;
  if (amt !== defaultErrorString) {
    val = percent(amt, precision);
    if (amt > 0) {
      val = `+${val}`;
    }
  }
  return val;
}

function convertStringToNumber(amt) {
  const val = amt === defaultErrorString ? 0 : amt;
  return Number(val);
}

//Adds precision if number needs two digits after decimal. Example 100.5 to 100.50. Lets 10.59 and 10 stay the same
function formatExtraZero(amt) {
  let result = defaultErrorString;
  if (amt) {
    result = amt.toString();
    const amtString = amt.toString();
    if (amtString.includes('.') && (amtString.length - amtString.indexOf('.') < 3)) {
      result = number(amt, 2);
    }
  }
  return result;
}

/*========================================
=            STRING FUNCTIONS            =
========================================*/
function camelSpace(str) {
  if (!str || !str.length) {
    return '';
  }
  return str.replace(/([A-Z])/g, ' $1').trim();
}

function string(str) {
  if (!str || str === '-32768') {
    return '--';
  }
  return str;
}

function name(str) {
  if (!str || str === '-32768') {
    return 'N/A';
  }
  return str;
}

function yearString(num) {
  if (num > 0 && num <= 1) {
    return `${num} year`;
  }
  return `${num} years`;
}

function removeTabsAndLineBreaks(str) {
  return str.replace(/\t|\n|\n\t|\t\n/g, '');
}

/*========================================
=            DATE FUNCTIONS            =
========================================*/

function fullDate(dateString) {
  let date = '';
  if (dateString) {
    const dateObject = new Date(dateString);
    dateObject.setDate(dateObject.getDate());

    date = dateObject.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
  return date;
}

function longDate(dateString) {
  let date = '';
  if (dateString) {
    const dateObject = new Date(dateString);
    dateObject.setDate(dateObject.getDate());

    date = dateObject.toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' });
  }
  return date;
}

function dateYear(dateString) {
  let year = '';
  if (dateString) {
    year = new Date(dateString).getFullYear();
  }
  return year;
}

//Takes in something of the form 2017-12-31
function shortDate(dateString) {
  let date = defaultErrorString;
  if (dateString && dateString !== defaultErrorString) {
    const datePieces = dateString.split('-');
    if (datePieces.length > 2) {
      let month = datePieces[1];
      if (month[0] === '0') {
        month = month[1];
      }
      date = `${month}/${datePieces[2]}/${datePieces[0]}`;
    }
  }
  return date;
}

//Can take in a pre-formatted date like 08/12/2018 or can take something of the form 2017-12-31
function shortDatePreFormatted(dateString) {
  let date = defaultErrorString;
  if (dateString && dateString.indexOf('/') > -1) {
    //This date is already formatted but remove any leading-0 from month/day
    const datePieces = dateString.split('/');
    date = datePieces.map(datePiece => {
      if (datePiece[0] === '0') {
        datePiece = datePiece[1];
      }
      return datePiece;
    }).join('/');
  } else if (dateString && dateString !== defaultErrorString) {
    const datePieces = dateString.split('-');
    if (datePieces.length > 2) {
      let month = datePieces[1];
      if (month[0] === '0') {
        month = month[1];
      }
      date = `${month}/${datePieces[2]}/${datePieces[0]}`;
    }
  }
  return date;
}

//Takes in a number representing a millisecond date and returns the exact date and time
//Used for putting timestamps on the header fragments
function dateTime(input) {
  let returnVal = defaultErrorString;
  if (input) {
    const date = new Date(input);
    returnVal = date.toLocaleString();
  }
  return returnVal;
}

function fromOADate(date) {
  const oaDate = new Date(1899, 11, 30);
  const millisecondsOfaDay = 24 * 60 * 60 * 1000;

  return new Date((date * millisecondsOfaDay) - (millisecondsOfaDay / 24) + Date.parse(oaDate));
}

function fromOADateToShortString(date) {
  const oaDate = fromOADate(date);
  const year = oaDate.getFullYear();
  const month = oaDate.getMonth();
  const day = oaDate.getDay();
  const formattedDate = shortDatePreFormatted(`${year}-${month}-${day}`);
  return formattedDate;
}

/*========================================
=            HELPER FUNCTIONS            =
========================================*/
function getZeroes(precision) {
  let zeroes = [];
  for (let i = 0; i < precision; i++) {
    zeroes.push('0');
  }
  zeroes = zeroes.join('');
  return zeroes;
}

export default {
  install(Vue) {
    Object.defineProperty(Vue.prototype, '$format', { value: formatHelpers });
  },
  formatHelpers
};
